import { ImagesWithTextScrolling, throttle } from 'theme';
import { animate, inView, stagger } from 'vendor';

class MyakmScrollStory extends ImagesWithTextScrolling {
  connectedCallback() {
    this._ucStoryAbort?.abort();
    this._ucStoryAbort = new AbortController();
    const signal = this._ucStoryAbort.signal;
    this._imageToTransitionItems = Array.from(this.querySelectorAll('.images-scrolling-desktop__media-wrapper > :not(:first-child)'));
    this._mainImage = this.querySelector('.images-scrolling-desktop__media-wrapper > :first-child');
    this._contentItems = Array.from(this.querySelectorAll('.images-scrolling__content'));
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onScroll = throttle(() => {
      if (!motion.matches && this._mainImage?.getBoundingClientRect().height) this._onScroll();
    });
    if (this._mainImage && this._imageToTransitionItems.length) {
      window.addEventListener('scroll', onScroll, { signal, passive: true });
      window.addEventListener('resize', onScroll, { signal, passive: true });
      motion.addEventListener('change', onScroll, { signal });
      onScroll();
    }
  }
  disconnectedCallback() {
    this._ucStoryAbort?.abort();
  }
  _onScroll() {
    const imageRect = this._mainImage.getBoundingClientRect();
    const threshold = imageRect.bottom - imageRect.height * .1;
    let current = 0;
    this._contentItems.forEach((content, index) => {
      if (content.getBoundingClientRect().top < threshold) current = index;
    });
    // Native scroll logic assumes consecutive steps. Reset future layers after
    // anchor jumps, restored scroll positions, or large keyboard/page scrolls.
    this._imageToTransitionItems.forEach((image, index) => {
      if (index + 1 <= current || !image.classList.contains('is-visible')) return;
      const content = this._contentItems[index + 1];
      [image, content].forEach(element => element.getAnimations().forEach(animation => animation.cancel()));
      image.classList.remove('is-visible');
      image.style.opacity = '0';
      image.style.removeProperty('clip-path');
      content.style.opacity = '0';
    });
    super._onScroll();
  }
}

if (!customElements.get('myakm-scroll-story')) customElements.define('myakm-scroll-story', MyakmScrollStory);

const initMotion = section => {
  if (section._ucMotionCleanup) return;
  const abort = new AbortController(), stops = [], animations = new Set();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const reveal = elements => {
    const items = elements.filter(element => !element.dataset.ucRevealed && !element.closest('[hidden]'));
    if (!items.length) return;
    items.forEach(element => { element.dataset.ucRevealed = 'true'; });
    if (motion.matches) return;
    const animation = animate(items, { opacity: [0, 1], transform: ['translateY(24px)', 'translateY(0)'] }, { duration: .65, delay: stagger(.09), easing: [.22, 1, .36, 1] });
    animations.add(animation);
    animation.finished.then(() => {
      animations.delete(animation);
      items.forEach(element => { element.style.removeProperty('opacity'); element.style.removeProperty('transform'); });
    }).catch(() => {});
  };
  section.querySelectorAll('[data-uc-motion-group]').forEach(group => {
    stops.push(inView(group, () => reveal(Array.from(group.querySelectorAll('[data-uc-reveal]'))), { margin: '0px 0px -5% 0px' }));
  });
  section.querySelectorAll('[data-uc-reveal]:not([data-uc-motion-group] [data-uc-reveal])').forEach(element => {
    stops.push(inView(element, () => reveal([element]), { margin: '0px 0px -5% 0px' }));
  });
  const stopAnimations = () => {
    animations.forEach(animation => animation.stop());
    animations.clear();
    section.querySelectorAll('[data-uc-reveal]').forEach(element => { element.style.removeProperty('opacity'); element.style.removeProperty('transform'); });
  };
  motion.addEventListener('change', () => { if (motion.matches) stopAnimations(); }, { signal: abort.signal });
  section.addEventListener('uc:results', () => {
    const group = section.querySelector('[data-uc-cards]');
    if (group && group.getBoundingClientRect().top < window.innerHeight) reveal(Array.from(group.querySelectorAll('[data-uc-reveal]')));
  }, { signal: abort.signal });
  section._ucMotionCleanup = () => { abort.abort(); stops.forEach(stop => stop()); stopAnimations(); delete section._ucMotionCleanup; };
};
const start = scope => {
  const sections = new Set();
  scope.querySelectorAll('[data-uc-motion-group],[data-uc-reveal]').forEach(element => { const section = element.closest('.shopify-section'); if (section) sections.add(section); });
  sections.forEach(initMotion);
};
start(document);
document.addEventListener('shopify:section:load', event => start(event.target));
document.addEventListener('shopify:section:unload', event => event.target._ucMotionCleanup?.());
